<template>
  <section class="game-intro" aria-labelledby="game-intro-title">
    <header class="game-intro__header">
      <span class="game-intro__eyebrow">Соперник найден</span>
      <h1 id="game-intro-title" class="game-intro__title">Сейчас начнётся дуэль</h1>
      <ProgressLine class="game-intro__progress" :value="progress" :height="4" width="62%" />
    </header>

    <div class="game-intro__card game-intro__card--top">
      <DuelPlayerCard :player="opponent" :compact="compact" />
    </div>

    <div class="game-intro__vs" aria-hidden="true">
      <span class="game-intro__vs-line"></span>
      <GlowRing :size="78" :rings="2" pulse>
        <span class="game-intro__vs-text">VS</span>
      </GlowRing>
      <span class="game-intro__vs-line game-intro__vs-line--right"></span>
    </div>

    <div class="game-intro__card game-intro__card--bottom">
      <DuelPlayerCard :player="me" :compact="compact" tag="Вы" tag-lime />
    </div>

    <footer class="game-intro__footer">
      <p class="game-intro__status" role="status">
        <span class="cb-spinner game-intro__spinner" aria-hidden="true"></span>
        {{ status }}
      </p>
      <Transition name="cb-fade">
        <AppButton v-if="canLeave" variant="ghost" size="sm" @click="emit('leave')">Уйти</AppButton>
      </Transition>
    </footer>
  </section>
</template>

<script setup>
/**
 * GameIntroPanel — фаза «intro» экрана игры (экран 4): «Объявление участников сессии»,
 * линия прогресса подключения, карточки соперника (сверху) и игрока (снизу), «VS».
 *
 * props: opponent, me (PlayerInfo | null), progress (0..1), status (текст под карточками),
 *        canLeave (показать «Уйти», если ожидание затянулось), compact.
 * emits: leave
 */
import AppButton from "@/components/ui/AppButton.vue";
import GlowRing from "@/components/ui/GlowRing.vue";
import ProgressLine from "@/components/ui/ProgressLine.vue";
import DuelPlayerCard from "./DuelPlayerCard.vue";

defineProps({
  opponent: { type: Object, default: null },
  me: { type: Object, default: null },
  progress: { type: Number, default: 0 },
  status: { type: String, default: "" },
  canLeave: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(["leave"]);
</script>

<style scoped>
.game-intro {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.game-intro__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 10px 0 14px;
  flex-shrink: 0;
}

.game-intro__eyebrow { font-size: 12px; letter-spacing: .12em; text-transform: uppercase; color: var(--cb-lime); }

.game-intro__title {
  font-size: 21px;
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.01em;
}

.game-intro__progress {
  max-width: 240px;
}

.game-intro__card {
  flex: 1 1 0;
  min-height: 0;
}

.game-intro__card--top {
  animation: game-intro-drop 0.6s var(--cb-ease) both;
}

.game-intro__card--bottom {
  animation: game-intro-rise 0.6s var(--cb-ease) 0.15s both;
}

.game-intro__vs {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 70px;
  margin: -6px 0;
  flex-shrink: 0;
  z-index: 1;
}

.game-intro__vs-line {
  flex: 1 1 auto;
  max-width: 120px;
  height: 1px;
  background: linear-gradient(90deg, rgba(221, 245, 90, 0) 0%, rgba(221, 245, 90, 0.45) 100%);
}

.game-intro__vs-line--right {
  background: linear-gradient(90deg, rgba(221, 245, 90, 0.45) 0%, rgba(221, 245, 90, 0) 100%);
}

.game-intro__vs-text {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: var(--cb-lime);
  text-shadow: var(--cb-text-glow);
  animation: cb-pop-in 0.5s var(--cb-ease) 0.35s both;
}

.game-intro__footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-height: 44px;
  padding-top: 10px;
  flex-shrink: 0;
}

.game-intro__status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--cb-text-2);
  font-size: 14px;
  text-align: center;
}

.game-intro__spinner {
  font-size: 14px;
}

@keyframes game-intro-drop {
  from {
    opacity: 0;
    transform: translateY(-24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes game-intro-rise {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
